import { Empty } from "components/Empty/Empty"
import { SearchInput } from "components/SearchInput/SearchInput"
import { Spinner } from "components/Spinner/Spinner"
import { useInfiniteScroll } from "hooks/useInfiniteScroll"
import { NoDataIcon } from "icons/NoDataIcon"
import { PatientItem } from "interface/patientType"
import { uniqBy } from "lodash-es"
import { useMemo, useRef } from "react"
import { useSearchParams } from "react-router"
import { usePatients } from "services/patients"
import { useUserRole } from "services/users"
import { cn } from "styles/cn"
import { PatientListItem } from "./PatientListItem"

export const PatientsList = () => {
	const targetScroll = useRef<HTMLDivElement>(null)
	const { data: { isProvider, isProviderAnon } = {} } = useUserRole()
	const [searchParams, setSearchParams] = useSearchParams(DEFAULT_SEARCH_PARAMS)
	const query = searchParams.get("query") ?? ""
	const {
		data: {
			pages: list = [],
			needsAttentionTotal = 0,
			needsReviewTotal = 0,
		} = {},
		hasNextPage,
		fetchNextPage,
		isFetched,
		isFetching,
		isFetchingNextPage,
	} = usePatients(query)
	const isLoading = isFetching || isFetchingNextPage
	const { attentionItems, patientList, reviewedPatientList } = useMemo(() => {
		const needReviewedList: PatientItem[] = []
		const reviewedList: PatientItem[] = []
		const attentionList: PatientItem[] = []

		list.forEach((item) => {
			if (
				item.unreadCommunicationDataCount > 0 ||
				item.supportTicketUpdateCount > 0 ||
				item.isAssignedToCallerAndUnopened ||
				item.authoredOverdueEscalationCount > 0
			) {
				attentionList.push(item)
			} else if (item.newDataPointsCount > 0) {
				needReviewedList.push(item)
			} else {
				reviewedList.push(item)
			}
		})

		return {
			patientList: uniqBy(needReviewedList, "id"),
			reviewedPatientList: uniqBy(reviewedList, "id"),
			attentionItems: uniqBy(attentionList, "id"),
		}
	}, [list])

	useInfiniteScroll({
		target: targetScroll.current,
		hasMore: hasNextPage,
		isLoading,
		onLoadMore: fetchNextPage,
	})

	return (
		<Spinner spinning={isLoading}>
			<div className="mx-auto flex h-full max-h-full max-w-300 flex-col items-center gap-4 overflow-hidden">
				<SearchInput
					value={query}
					placeholder="Search patients"
					onSearch={(query) => setSearchParams({ query })}
				/>

				<div
					ref={targetScroll}
					className={cn(
						"flex size-full flex-col gap-5 overflow-y-auto bg-white pr-0.5 pb-6",
						!isProvider && "mt-5",
					)}
				>
					{needsAttentionTotal > 0 && (
						<>
							<h2 className="flex items-center gap-2">
								<span className="text-xl">Needs attention</span>
								<span className="flex items-center justify-start gap-1 rounded-2xl bg-yellow-orange-20 px-2 text-yellow-orange-80">
									{needsAttentionTotal}
								</span>
							</h2>

							<ul
								className="flex w-full flex-col gap-3"
								aria-label={`${needsAttentionTotal} patients with updates`}
							>
								{attentionItems.map((item) => {
									return <PatientListItem key={item.id} item={item} />
								})}
							</ul>
						</>
					)}

					{needsReviewTotal > 0 && (
						<h2 className="flex items-center gap-2">
							<span className="text-xl">
								{isProviderAnon ? "All patients" : "Needs review"}
							</span>
							<span className="flex items-center justify-start gap-1 rounded-2xl bg-blue-0 px-2 text-blue-60">
								{needsReviewTotal}
							</span>
						</h2>
					)}

					{patientList.length > 0 && (
						<ul
							className="flex w-full flex-col gap-3"
							aria-label={`${patientList.length} patients${!isProviderAnon ? " with screenings needing review" : ""}`}
						>
							{patientList.map((item) => (
								<PatientListItem key={item.id} item={item} />
							))}
						</ul>
					)}

					{!!reviewedPatientList.length && (
						<h2 className="text-xl">Reviewed</h2>
					)}

					{reviewedPatientList.length > 0 && (
						<ul
							className="flex w-full flex-col gap-3"
							aria-label={`${reviewedPatientList.length} patients with reviewed screenings`}
						>
							{reviewedPatientList.map((item) => (
								<PatientListItem key={item.id} item={item} />
							))}
						</ul>
					)}

					{list.length === 0 && isFetched && (
						<Empty
							Icon={NoDataIcon}
							title="No data"
							description={!!query ? "No patients match the search query" : ""}
						/>
					)}
				</div>
			</div>
		</Spinner>
	)
}

const DEFAULT_SEARCH_PARAMS = { query: "" }
